import axios, { AxiosInstance } from "axios";
import { ILogger } from "./Logger";

export class GazetteerService {
    private _gazetteerUrl: string | undefined;
    private _gazetteerToken: string | undefined;
    private _axios: AxiosInstance;
    private _logger: ILogger | null;

    constructor(gazetteerUrl?: string, gazetteerToken?: string, logger?: ILogger) {
        this._logger = logger ? logger : null;
        this._gazetteerToken = gazetteerToken;
        this._gazetteerUrl = gazetteerUrl;
        this._axios = axios.create({ baseURL: this._gazetteerUrl });
        this._axios.interceptors.request.use(
            (config) => {
                this._logger && this._logger.info("requesting url " + config.baseURL + config.url);
                return config;
            },
            (error) => {
                this._logger && this._logger.error(`error requesting gazetteerUrl; `, this._gazetteerUrl, `error: `, error);
            }
        );
    }

    async batchGeocode(addresses: string[]): Promise<GeocodeMatchedAddress[]> {
        const batchGeocodeEndpoint = "batchgeocode";
        const url = `${batchGeocodeEndpoint}?token=${this._gazetteerToken}`;
        const addressesParamsUrlEncoded = new URLSearchParams();
        addresses.forEach((address) => addressesParamsUrlEncoded.append("addresses", address));
        try {
            const result = await this._axios.post(url, addressesParamsUrlEncoded, {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            });
            const geocodeData: GeocodeMatchedAddress[] = result.data;
            if (!Array.isArray(geocodeData) || !geocodeData) {
                this._logger?.error(`error requesting gazetteerUrl; `, this._gazetteerUrl, `empty data `, geocodeData);
                return [];
            }
            geocodeData.forEach((address) => {
                address.closestMatches.forEach((match: ClosestMatch, idx) => {
                    address.closestMatches[idx].record = JSON.parse(match.record as any); // record is a string here before being parsed
                });
            });
            return geocodeData;
        } catch (e) {
            this._logger?.error(`error requesting gazetteerUrl; `, this._gazetteerUrl, `error: `, e);
            return [{ address: "", closestMatches: [] }];
        }
    }

    async getGazetteerAddresses(value: string | null | undefined): Promise<GazetteerAddresses[] | null> {
        if (!value) {
            return null;
        }
        const res = await this._axios.get(`suggest?token=${this._gazetteerToken}&searchedTerm=${encodeURIComponent(value)}`);
        const data = res.data;
        if (!data || !data.addresses.length || !data.ids.length) {
            return null;
        }
        const addresses: GazetteerAddresses[] = data.addresses.map((address: string, index: number) => {
            return {
                label: address,
                id: data.ids[index],
            };
        });
        return addresses;
    }

    async getAddressById(id: string | null | undefined | number) {
        if (!id) {
            return null;
        }
        const res = await this._axios.get(`${id}?token=${this._gazetteerToken}`);
        const data: GazetteerAddressDetails = res.data;
        if (data) {
            return data;
        }
        return null;
    }
}
export interface GazetteerAddresses {
    label: string;
    id: string;
}

/**
 * @type {GazetteerAddressDetails}
 * @property {record}: Parse to get GazetteerRecordParsed interface.
 */
export interface GazetteerAddressDetails {
    address: string;
    point: GazetteerPoint;
    record: string;
}

export type GazetteerRecordParsed = {
    UPRN: string;
    USRN: string;
    SAO_START_NUMBER: string;
    SAO_START_SUFFIX: string;
    SAO_END_NUMBER: string;
    SAO_END_SUFFIX: string;
    SAO_TEXT: string;
    PAO_START_NUMBER: string;
    PAO_START_SUFFIX: string;
    PAO_END_NUMBER: string;
    PAO_END_SUFFIX: string;
    PAO_TEXT: string;
    STREET_DESCRIPTOR: string;
    POSTCODE: string;
    TOWN_NAME: string;
    LANGUAGE: string;
    LOCALITY_NAME: string;
    ADMINISTRATIVE_AREA: string;
    EASTING: string;
    NORTHING: string;
    ADDRESS_LOCATION: string;
    LOGICAL_STATUS: string;
    BLPU_CLASSIFICATION: string;
    GEO_SINGLE_ADDRESS_LABEL: string;
    MI_PRINX: string;
    ORGANISATION?: string;
    LOCALITY?: string;
};

export interface GeocodeMatchedAddress {
    address: string;
    closestMatches: ClosestMatch[];
}

/**
 * @type {ClosestMatch}
 * @property {id}: Match ID
 * @property {address}: Full address
 * @property {record}: GazetteerRecordParsed
 * @property {point}: GazetteerPoint
 * @property {scoring}
 */
export interface ClosestMatch {
    id: number;
    address: string;
    record: GazetteerRecordParsed;
    point: GazetteerPoint;
    scoring: number;
}

export interface GazetteerPoint {
    easting: string;
    northing: string;
}
